import React from 'react';

import {
  Link,
  RyeconErrorOutline,
  RyeconInfoOutline,
  Tooltip,
} from '@dbt-labs/sourdough';

import { ModelQueryHistoryStatus } from '@/__generated__/graphql';
import { CustomCell } from '@/components/Table/CustomCell';
import { OneLineHeader, TwoLineHeader } from '@/components/Table/TableHeader';
import { RightAlignedTruncatedCell } from '@/components/TruncatedCell';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useModalProvider } from '@/context/ModalProvider';
import { QUERY_USAGE_TOOLTIP } from '@/hooks/discovery/appliedModels';
import { useLinkToDbtCloud } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { getHumanReadableQueryError } from '@/util/formatErrors';
import { getNotEnabledMessage } from '@/util/queryHistoryModalMessage';

export const queryHistoryCellConfig = (
  queryHistoryState?: ModelQueryHistoryStatus,
  queryHistoryAvailableNotEnabled?: boolean,
) => {
  if (queryHistoryAvailableNotEnabled) {
    return () => (
      <CustomCell
        children={<span className="text-fgDecorative">n/a</span>}
        tooltip=""
        disable={true}
        displayDisabledChildren={true}
      />
    );
  }

  switch (queryHistoryState) {
    case ModelQueryHistoryStatus.Success:
      return RightAlignedTruncatedCell;
    case ModelQueryHistoryStatus.NoData:
      return () => (
        <CustomCell
          children={<span className="text-fgDecorative">Pending...</span>}
          tooltip=""
          disable={true}
          displayDisabledChildren={true}
        />
      );
    case ModelQueryHistoryStatus.Error:
      return () => (
        <CustomCell
          children={<span className="text-fgDecorative">Unknown</span>}
          tooltip=""
          disable={true}
          displayDisabledChildren={true}
        />
      );
  }
};

const NoDataTooltip: React.FC = () => (
  <div>
    This is enabled but gathering usage consumption queries may take up to 24 hours to
    display.
  </div>
);

const ErrorModalContent: React.FC<{ errorMessage: string | null }> = ({
  errorMessage,
}) => (
  <>
    <span data-testid="error-message">
      An error occurred loading usage data: '{errorMessage}'. <br />
      Learn how to troubleshoot permissions{' '}
      <Link
        isInternal={false}
        to="https://docs.getdbt.com/docs/collaborate/model-query-history#credential-permissions"
        onClick={(e) => e.stopPropagation()}
        shouldOpenNewTab
      >
        here
      </Link>
      .
    </span>
  </>
);

const InfoModalContent: React.FC<{ infoMessage: React.ReactNode }> = ({
  infoMessage,
}) => (
  <>
    <span data-testid="info-message">{infoMessage}</span>
  </>
);

interface QueryHistoryTableHeaderProps {
  queryHistoryState?: ModelQueryHistoryStatus;
  error?: string | null;
  errorCategory?: string | null;
  queryHistoryAvailableNotEnabled: boolean;
}

const QueryHistoryTableHeader: React.FC<QueryHistoryTableHeaderProps> = ({
  queryHistoryState,
  error,
  errorCategory,
  queryHistoryAvailableNotEnabled,
}) => {
  const { openModal } = useModalProvider();
  const errorMessage = getHumanReadableQueryError(errorCategory, error);
  const { isAdmin, hasDeveloperLicense, environmentId } = useDbtCloudProject();
  const projectScope = useUrlProjectParams();
  const projectIdFromUrl = projectScope.projectId;
  const getEnvironmentLink = useLinkToDbtCloud('environmentDetailSettings');
  const link = getEnvironmentLink
    ? getEnvironmentLink({
        ...projectScope,
        projectId: projectIdFromUrl,
        environmentId: environmentId,
      })
    : undefined;

  if (queryHistoryAvailableNotEnabled) {
    const infoMessage = getNotEnabledMessage(isAdmin, hasDeveloperLicense, link);
    return (
      <TwoLineHeader
        title="Query count"
        subtitle={
          <div className="flex items-center space-x-1">
            <span className="text-fgBrand">Requires Setup</span>
            <Link
              testId="usage-error-link"
              isInternal={true}
              className="text-fgBrand hover:text-fgBrandHover"
              onClick={() => {
                openModal(
                  'Requires setup to view',
                  'info',
                  <InfoModalContent infoMessage={infoMessage} />,
                );
              }}
              to="#"
            >
              <RyeconInfoOutline className="text-fgBrand" />
            </Link>
          </div>
        }
      />
    );
  }

  switch (queryHistoryState) {
    case ModelQueryHistoryStatus.NoData:
      return (
        <TwoLineHeader
          title="Query count"
          subtitle={
            <div className="flex items-center space-x-1">
              <span className="text-fgInfo">Pending Data</span>
              <Tooltip content={<NoDataTooltip />} placement="top-end">
                <RyeconInfoOutline className="text-fgInfo" />
              </Tooltip>
            </div>
          }
        />
      );
    case ModelQueryHistoryStatus.Error:
      return (
        <TwoLineHeader
          title="Query count"
          subtitle={
            <div className="flex items-center space-x-1">
              <span className="text-fgDanger">Unknown Error</span>
              <Link
                testId="usage-error-link"
                isInternal={true}
                onClick={() => {
                  openModal(
                    'Consumption Error',
                    'alert',
                    <ErrorModalContent errorMessage={errorMessage} />,
                  );
                }}
                to="#"
              >
                <RyeconErrorOutline className="text-fgDanger" />
              </Link>
            </div>
          }
        />
      );
    default:
      return (
        <OneLineHeader
          title={
            <span className="flex space-x-1 align-middle">
              <span>Query count</span>
              <Tooltip
                content={
                  <div className="whitespace-normal text-wrap">
                    {QUERY_USAGE_TOOLTIP}
                  </div>
                }
                placement="top-end"
              >
                <RyeconInfoOutline />
              </Tooltip>
            </span>
          }
          align="right"
        />
      );
  }
};

export default QueryHistoryTableHeader;
